"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

import { useAnalyticsConsent } from "@/components/analytics/analytics-provider";
import { i18n, isLocale, type Locale } from "@/i18n-config";
import { defineLocaleRecord } from "@/i18n-config";

type ConsentCopy = {
  message: string;
  learnMore: string;
  reject: string;
  accept: string;
};

const CONSENT_COPY = defineLocaleRecord<ConsentCopy>({
  en: {
    message: "We use analytics cookies to understand usage and improve Zipvibe.",
    learnMore: "Cookie policy",
    reject: "Reject",
    accept: "Accept analytics",
  },
  de: {
    message: "Wir nutzen Analyse-Cookies, um die Nutzung zu verstehen und Zipvibe zu verbessern.",
    learnMore: "Cookie-Richtlinie",
    reject: "Ablehnen",
    accept: "Analytics erlauben",
  },
  fr: {
    message: "Nous utilisons des cookies d'analyse pour comprendre l'usage et ameliorer Zipvibe.",
    learnMore: "Politique cookies",
    reject: "Refuser",
    accept: "Accepter l'analyse",
  },
  es: {
    message: "Usamos cookies de analitica para entender el uso y mejorar Zipvibe.",
    learnMore: "Politica de cookies",
    reject: "Rechazar",
    accept: "Aceptar analitica",
  },
  it: {
    message: "Usiamo cookie di analisi per capire l'uso e migliorare Zipvibe.",
    learnMore: "Cookie policy",
    reject: "Rifiuta",
    accept: "Accetta analisi",
  },
  uk: {
    message: "Ми використовуємо аналітичні cookie, щоб розуміти використання і покращувати Zipvibe.",
    learnMore: "Політика cookie",
    reject: "Відхилити",
    accept: "Прийняти аналітику",
  },
  ru: {
    message: "Мы используем аналитические cookie, чтобы понимать использование и улучшать Zipvibe.",
    learnMore: "Политика cookie",
    reject: "Отклонить",
    accept: "Принять аналитику",
  },
});

function resolveLocale(pathname: string | null): Locale {
  const firstSegment = pathname?.split("/")[1] ?? "";
  if (isLocale(firstSegment)) {
    return firstSegment;
  }

  return i18n.defaultLocale;
}

function resolveCookiePath(pathname: string | null): string {
  const firstSegment = pathname?.split("/")[1] ?? "";
  if (isLocale(firstSegment)) {
    return `/${firstSegment}/cookies`;
  }

  return `/${i18n.defaultLocale}/cookies`;
}

export function CookieConsentBanner() {
  const pathname = usePathname();
  const locale = resolveLocale(pathname);
  const copy = CONSENT_COPY[locale];
  const cookiesPath = resolveCookiePath(pathname);
  const { consent, setConsent } = useAnalyticsConsent();

  if (consent !== null) {
    return null;
  }

  return (
    <aside className="fixed inset-x-0 bottom-0 z-[80] px-3 pb-3 sm:px-4 sm:pb-4" aria-live="polite">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 rounded-2xl border border-[#d6c8af] bg-[#fff9ef] p-4 shadow-[0_18px_50px_rgba(38,57,49,0.18)] sm:flex-row sm:items-center sm:justify-between sm:gap-5 sm:p-5">
        <p className="text-sm leading-relaxed text-[#32463f]">
          {copy.message}{" "}
          <Link href={cookiesPath} className="font-semibold text-[#0f766e] underline-offset-2 hover:underline">
            {copy.learnMore}
          </Link>
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setConsent("rejected")}
            className="inline-flex min-h-10 items-center justify-center rounded-lg border border-[#cdbb9d] bg-[#f4ecdf] px-3 text-sm font-medium text-[#4a5f58] transition-colors hover:bg-[#ecdfcc]"
          >
            {copy.reject}
          </button>
          <button
            type="button"
            onClick={() => setConsent("accepted")}
            className="inline-flex min-h-10 items-center justify-center rounded-lg border border-[#0f766e]/30 bg-[#0f766e] px-3 text-sm font-semibold text-white transition-colors hover:bg-[#0d6962]"
          >
            {copy.accept}
          </button>
        </div>
      </div>
    </aside>
  );
}
